import React from 'react'
import styled from 'styled-components'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import { padding, paddingSizes } from '@jarvis.oddle/core/theme/default'
import { status as productStatus } from '@jarvis.oddle/core/tools/product'
import modalController from 'src/decorators/ModalController'
import toast from '@jarvis.oddle/core/components/Toast'
import { Column } from '@jarvis.oddle/core/components/Grid'
import {
  ProductGridCard,
  ProductListCard
} from 'src/components/molecules/ProductCards'
import pickUpDeliveryFlow from 'src/flows/pickUpDeliveryFlow'
import AddToCartToast from 'src/components/molecules/AddToCartToast'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import { useRouter } from 'next/router'
import useTracker from 'src/hooks/useTracker'
import { addItemToCartAction } from 'src/redux/order/action'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectMenuMode } from 'src/redux/meta'

const StyledColumn = styled(Column).attrs(({ listView, showSideNav }) => ({
  sm: listView ? 12 : 6,
  lg: listView ? (showSideNav ? 12 : 6) : showSideNav ? 4 : 3 // 12-based
}))`
  padding: ${padding[paddingSizes.extraSmall]};
  ${breakpoint('desktop')`
    padding: ${padding[paddingSizes.small]};
  `}
`

interface ProductCardProps {
  showImage: boolean
  showDefaultImage: boolean
  productImageAspect: string
  listView: boolean
  showSideNav: boolean
  product: Menu.MenuItem
  category: Menu.MenuData
}

const ProductCard = ({
  showImage,
  showDefaultImage,
  productImageAspect,
  listView,
  showSideNav,
  product,
  category
}: ProductCardProps): JSX.Element => {
  const router = useRouter()
  const Card = listView ? ProductListCard : ProductGridCard
  const { t } = React.useContext(TranslationContext)
  const { notifyAddToCart, notifyViewContent } = useTracker()
  const dispatch = useAppDispatch()
  const menuMode = useAppSelector(selectMenuMode)

  const handleAddButtonClick = React.useCallback(
    (e: React.FormEventHandler<HTMLInputElement>) => {
      const addItemToCart = async (e) => {
        if (menuMode === 'preview') {
          e.stopPropagation()
          e.preventDefault()
          modalController.showModal('ErrorModal', {
            error: t('Cannot add item to cart on preview mode')
          })
          return
        }
        if (
          product.status === productStatus.AVAILABLE &&
          product.variants.length === 1 &&
          product.subitems.length === 0
        ) {
          e.stopPropagation()
          e.preventDefault()
          if (!pickUpDeliveryFlow.getMeta().isVisited) {
            e.persist()
            pickUpDeliveryFlow.start({
              onFinished: () => addItemToCart(e)
            })
          } else {
            const variant = product.variants[0]
            const addItem = await dispatch(
              addItemToCartAction({
                orderItem: { variantId: variant.id, quantity: 1, subitems: [] },
                menuItem: product,
                finalPrice: variant.price
              })
            )

            if (
              addItem.meta.requestStatus === 'fulfilled' &&
              addItem.payload.payload.ok
            ) {
              notifyAddToCart(addItem.payload)
              const items = [
                {
                  name: product.name,
                  quantity: 1,
                  price: variant.price,
                  imageUrl: product.imageFullUrls?.[0] || product.imageUrls?.[0]
                }
              ]

              toast(<AddToCartToast items={items} />, {
                slideInAnimation: 'top',
                closeOnPageClick: true
              })
            } else {
              modalController.showModal('ErrorModal', {
                error: t(
                  addItem.payload.error || addItem.payload?.payload?.error
                )
              })
            }
          }
        }
      }

      addItemToCart(e)
    },
    [dispatch, menuMode, notifyAddToCart, product, t]
  )

  const landingPageUrl = router.query.slug
    ? '/[locale]/menus/[slug]/'
    : '/[locale]/'
  const asHref = router.query.slug
    ? `/${router.query.locale}/menus/${router.query.slug}/?productId=${product.id}`
    : `/${router.query.locale}/?productId=${product.id}`

  const showProductDetailsModal = React.useCallback(() => {
    router.push(landingPageUrl, asHref, { shallow: true })
    notifyViewContent(product.id)
    modalController.showModal('ProductDetailsModal', {
      product,
      category,
      id: product.id
    })
  }, [router, landingPageUrl, asHref, notifyViewContent, product, category])

  return (
    <StyledColumn listView={listView} showSideNav={showSideNav}>
      <a onClick={showProductDetailsModal}>
        <Card
          product={product}
          category={category}
          showImage={showImage}
          showDefaultImage={showDefaultImage}
          productImageAspect={productImageAspect}
          onButtonClick={handleAddButtonClick}
        />
      </a>
    </StyledColumn>
  )
}

export default ProductCard
